import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import MDTypography from "components/MDTypography";
import PortalSidebar from "components/portal/PortalSidebar";
import usePortalTheme from "components/portal/usePortalTheme";
import { portalSidebarSections } from "components/portal/portalNav";
import { useAuth } from "context/AuthContext";
import { useCart } from "context/CartContext";
import { useStorefrontTenant } from "context/StorefrontTenantContext";
import { isDistributorRole } from "lib/portalRoles";
import { resolveStorefrontAssetUrl } from "lib/storefrontTheme";
import useStorefrontCopy from "hooks/useStorefrontCopy";
import shortLogo from "assets/images/logos/ShortLogo.png";

export default function CustomerPortalLayout({ title, subtitle, actions, children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const [mobileOpen, setMobileOpen] = useState(false);

  const { pluginRole } = useAuth();
  const { itemCount } = useCart();
  const { tenant } = useStorefrontTenant();
  const { t } = useStorefrontCopy();
  const p = usePortalTheme();
  const distributor = isDistributorRole(pluginRole);

  const logoUrl = resolveStorefrontAssetUrl(tenant?.brandLogoUrl) || shortLogo;
  const brandName = String(tenant?.name || t("common.brandFallback", "Storefront")).trim();

  const roleLabel = distributor
    ? t("portal.distributorPortal", "Distributor portal")
    : t("portal.customerPortal", "Customer portal");

  const sections = useMemo(() => portalSidebarSections(t, distributor), [t, distributor]);

  const handleNavigate = (route) => {
    setMobileOpen(false);
    navigate(route);
  };

  const sidebar = (
    <PortalSidebar
      logoUrl={logoUrl}
      brandName={brandName}
      roleLabel={roleLabel}
      sections={sections}
      pathname={location.pathname}
      onNavigate={handleNavigate}
      onSignOut={() => handleNavigate("/sign-out")}
      signOutLabel={t("common.signOut", "Sign out")}
      accountLabel={t("portal.navAccount", "Account")}
      storeLabel={t("portal.navStore", "Store")}
      cartCount={itemCount}
    />
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: p.canvas, color: p.ink, display: "flex" }}>
      {isDesktop ? (
        <Box
          sx={{
            width: p.railWidth,
            flexShrink: 0,
            position: "sticky",
            top: 0,
            height: "100vh",
          }}
        >
          {sidebar}
        </Box>
      ) : (
        <Drawer
          anchor="left"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          PaperProps={{
            sx: { width: p.railWidth, maxWidth: "86vw", border: 0 },
          }}
        >
          {sidebar}
        </Drawer>
      )}

      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        {!isDesktop ? (
          <Box
            sx={{
              px: 2,
              py: 1.25,
              bgcolor: p.surface,
              borderBottom: `1px solid ${p.lineStrong}`,
              display: "flex",
              alignItems: "center",
              gap: 1,
              position: "sticky",
              top: 0,
              zIndex: 1100,
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label={t("portal.openMenu", "Open menu")}
              sx={{
                margin: 0,
                border: `1px solid ${p.lineStrong}`,
                borderRadius: `${p.radiusSm}px`,
                bgcolor: p.surface,
                width: 40,
                height: 40,
                display: "grid",
                placeItems: "center",
                cursor: "pointer",
                color: p.ink,
                flexShrink: 0,
              }}
            >
              <Icon>menu</Icon>
            </Box>
            <MDTypography
              variant="subtitle2"
              sx={{ fontWeight: 800, color: p.ink, flex: 1, minWidth: 0 }}
            >
              {title}
            </MDTypography>
            {actions ? (
              <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0, flexWrap: "wrap" }}>
                {actions}
              </Stack>
            ) : null}
          </Box>
        ) : null}

        <Box
          component="main"
          sx={{
            flex: 1,
            px: { xs: 2, md: 3, xl: 4 },
            py: { xs: 2.5, md: 3.5 },
          }}
        >
          <Box
            sx={{
              maxWidth: 1120,
              mx: "auto",
            }}
          >
            {isDesktop ? (
              <Box
                sx={{
                  mb: 3,
                  display: "flex",
                  flexDirection: { md: "row" },
                  alignItems: { md: "flex-end" },
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <MDTypography
                    variant="h3"
                    sx={{
                      color: p.ink,
                      fontWeight: 800,
                      fontSize: { md: "1.875rem", xl: "2.125rem" },
                      lineHeight: 1.12,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {title}
                  </MDTypography>
                  {subtitle ? (
                    <MDTypography
                      variant="body1"
                      sx={{ color: p.muted, mt: 0.75, maxWidth: 640, lineHeight: 1.6 }}
                    >
                      {subtitle}
                    </MDTypography>
                  ) : null}
                </Box>
                {actions ? (
                  <Stack direction="row" spacing={1} sx={{ flexShrink: 0, flexWrap: "wrap" }}>
                    {actions}
                  </Stack>
                ) : null}
              </Box>
            ) : subtitle ? (
              <MDTypography variant="body2" sx={{ color: p.muted, mb: 2, lineHeight: 1.6 }}>
                {subtitle}
              </MDTypography>
            ) : null}

            <Box sx={{ minWidth: 0 }}>{children}</Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

CustomerPortalLayout.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  actions: PropTypes.node,
  children: PropTypes.node.isRequired,
};

CustomerPortalLayout.defaultProps = {
  subtitle: null,
  actions: null,
};
